import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MapPin, Loader2, Truck, ShieldCheck, ClipboardList, PackageOpen, X, Navigation as NavIcon, Phone, User, Car, Star, CheckCircle2 } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { YhCustomerLinked } from "@/components/YhCustomerLinked";
import LocationPicker from "@/components/LocationPicker";
import { TRANSPORT_REQUEST_STATUS_TR, OFFER_STATUS_TR, TRANSPORT_STEPS, transportStepIndex, trStatusVariant } from "@/lib/labels";

const CATS = ["Otomobil", "SUV / Pickup", "Hafif ticari", "Kamyon", "Tır / Çekici", "Otobüs / Minibüs", "İş makinesi", "Traktör", "Forklift", "Diğer"];
const CONDS = ["Çalışıyor", "Çalışmıyor", "Hasarlı"];

const EMPTY = {
  pickup_province: "", pickup_district: "", pickup_address: "", pickup_lat: 0, pickup_lng: 0,
  delivery_province: "", delivery_district: "", delivery_address: "", delivery_lat: 0, delivery_lng: 0,
  deadline_at: "", transport_category: "Otomobil", quantity: 1, running_condition: "Çalışıyor",
  make_model: "", description: "",
};

export default function TransportCustomer({ defaultTab = "new" }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || defaultTab);
  const [f, setF] = useState(EMPTY);
  const [provinces, setProvinces] = useState([]);
  const [puDist, setPuDist] = useState([]);
  const [deDist, setDeDist] = useState([]);
  const [mine, setMine] = useState([]);
  const [offersByReq, setOffersByReq] = useState({});
  const [busy, setBusy] = useState(false);
  const [detailId, setDetailId] = useState(searchParams.get("req") || null);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [hasYh, setHasYh] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    if (user.role !== "user") { nav("/"); return; }
    api.get("/locations/provinces").then(({ data }) => setProvinces(data));
    loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, nav]);

  // 30s silent background refresh — never resets forms/modals, never reloads page
  useEffect(() => {
    if (!user || user.role !== "user") return;
    const id = setInterval(() => { loadMine(); }, 30000);
    const onVis = () => { if (document.visibilityState === "visible") loadMine(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => { if (f.pickup_province) api.get("/locations/districts", { params: { province: f.pickup_province } }).then(({ data }) => setPuDist(data)); else setPuDist([]); }, [f.pickup_province]);
  useEffect(() => { if (f.delivery_province) api.get("/locations/districts", { params: { province: f.delivery_province } }).then(({ data }) => setDeDist(data)); else setDeDist([]); }, [f.delivery_province]);

  const loadMine = async () => {
    const { data } = await api.get("/transport/requests/mine");
    setMine(data);
    const om = {};
    for (const r of data) {
      try { const { data: offs } = await api.get(`/transport/requests/${r.id}/offers`); om[r.id] = offs; } catch { om[r.id] = []; }
    }
    setOffersByReq(om);
  };

  const changeTab = (v) => {
    setTab(v);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", v); sp.delete("req");
    setSearchParams(sp, { replace: true });
    setDetailId(null);
  };

  const upd = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const grabLoc = (which) => {
    if (!navigator.geolocation) return toast.error("Tarayıcı konum desteklemiyor");
    navigator.geolocation.getCurrentPosition((p) => {
      setF((s) => ({ ...s, [`${which}_lat`]: p.coords.latitude, [`${which}_lng`]: p.coords.longitude }));
      toast.success("Konum alındı");
    }, () => toast.error("Konum alınamadı"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!f.pickup_province || !f.pickup_district) return toast.error("Alım il/ilçe gerekli");
    if (!f.delivery_province || !f.delivery_district) return toast.error("Teslim il/ilçe gerekli");
    if (!f.deadline_at) return toast.error("Son alım tarihi gerekli");
    setBusy(true);
    try {
      const payload = {
        ...f,
        deadline_at: new Date(f.deadline_at).toISOString(),
        quantity: Number(f.quantity) || 1,
        pickup_lat: f.pickup_lat || null,
        pickup_lng: f.pickup_lng || null,
        delivery_lat: f.delivery_lat || null,
        delivery_lng: f.delivery_lng || null,
      };
      await api.post("/transport/requests", payload);
      toast.success("Taşıma talebi oluşturuldu");
      setF(EMPTY);
      await loadMine();
      changeTab("track");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmDelivery = async (rid) => {
    if (!window.confirm("Teslimatın alındığını onaylıyor musunuz? Bu adımdan sonra tamamlanmış sayılır ve puanlama açılır.")) return;
    try { await api.post(`/transport/requests/${rid}/confirm-delivery`); toast.success("Teslimat onaylandı"); loadMine(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const submitTransportReview = async (rid, rating, comment) => {
    try {
      await api.post("/reviews", { transport_request_id: rid, rating, comment });
      toast.success("Değerlendirmeniz alındı, teşekkürler");
      setReviewFor(null);
      loadMine();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const acceptOffer = async (oid) => {
    if (!window.confirm("Bu teklifi kabul ediyorsunuz. Kesin adres kabul edilen firmayla paylaşılacak.")) return;
    try { await api.post(`/transport/offers/${oid}/accept`); toast.success("Teklif kabul edildi"); loadMine(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const rejectOffer = async (oid) => {
    try {
      await api.post(`/transport/offers/${oid}/reject`);
      loadMine();
    } catch (err) {
      console.warn("[rejectOffer]", err?.response?.status || err?.message);
      toast.error(formatErr(err?.response?.data?.detail) || "Teklif reddedilemedi");
    }
  };
  const cancelReq = async (rid) => {
    if (!window.confirm("Talebi iptal et?")) return;
    try {
      await api.post(`/transport/requests/${rid}/cancel`);
      loadMine();
    } catch (err) {
      console.warn("[cancelReq]", err?.response?.status || err?.message);
      toast.error(formatErr(err?.response?.data?.detail) || "Talep iptal edilemedi");
    }
  };

  const openDetail = (rid) => {
    setDetailId(rid);
    const sp = new URLSearchParams(searchParams);
    sp.set("req", rid); sp.set("tab", "track");
    setSearchParams(sp, { replace: true });
  };
  const closeDetail = () => {
    setDetailId(null);
    const sp = new URLSearchParams(searchParams);
    sp.delete("req");
    setSearchParams(sp, { replace: true });
  };

  const detailReq = useMemo(() => mine.find((r) => r.id === detailId), [mine, detailId]);
  const detailOffers = detailId ? (offersByReq[detailId] || []) : [];
  const acceptedOffer = detailReq ? detailOffers.find((o) => o.status === "accepted") : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      {/* Branded header */}
      <div className="relative overflow-hidden rounded-2xl mb-4 bg-[hsl(var(--brand-black))] p-5 sm:p-6">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FFC107 0px, transparent 120px), radial-gradient(circle at 85% 80%, #FFC107 0px, transparent 160px)" }} />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
            <Truck className="w-6 h-6 text-primary" />
          </div>
          <div className="text-white">
            <div className="flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest">
              <ShieldCheck className="w-3 h-3" /> YOL HATTI · TAŞIMA
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight">Taşıma Merkezi</h1>
            <p className="text-xs text-white/70 mt-1">Yeni talep açın, mevcut taleplerinizi takip edin.</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="new" data-testid="tc-tab-new"><Truck className="w-4 h-4 mr-1" /> Yeni Talep Oluştur</TabsTrigger>
          <TabsTrigger value="track" data-testid="tc-tab-track"><ClipboardList className="w-4 h-4 mr-1" /> Taleplerim {mine.length > 0 && <Badge variant="secondary" className="ml-1">{mine.length}</Badge>}</TabsTrigger>
        </TabsList>

        {/* ================= YENİ TALEP ================= */}
        <TabsContent value="new">
          <div className="grid grid-cols-3 gap-2 mb-4 text-center text-[11px] sm:text-xs">
            <div className="rounded-2xl border p-3">
              <ClipboardList className="w-5 h-5 mx-auto text-primary mb-1" />
              <div className="font-semibold">1. Talep aç</div>
              <div className="text-muted-foreground">Nereden – nereye</div>
            </div>
            <div className="rounded-2xl border p-3">
              <PackageOpen className="w-5 h-5 mx-auto text-primary mb-1" />
              <div className="font-semibold">2. Teklif topla</div>
              <div className="text-muted-foreground">Firmalar fiyat verir</div>
            </div>
            <div className="rounded-2xl border p-3">
              <ShieldCheck className="w-5 h-5 mx-auto text-primary mb-1" />
              <div className="font-semibold">3. Kabul et</div>
              <div className="text-muted-foreground">Adres kabul sonrası paylaşılır</div>
            </div>
          </div>

          <form onSubmit={submit} className="rounded-2xl border p-4 space-y-3 mb-6" data-testid="tc-form">
            <h2 className="font-semibold">Yeni Taşıma Talebi</h2>
            <div className="rounded-xl bg-primary/10 border border-primary/40 p-2 text-xs flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <span>Gizlilik: kesin adres ve harita konumu yalnızca kabul edilen firmayla paylaşılır. Diğer firmalar sadece il/ilçe görür. Serbest metin alanlarına telefon veya iletişim bilgisi yazmayın; sistem reddeder.</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl border p-3 space-y-2">
                <div className="font-semibold text-sm">Alım (Nereden)</div>
                <SearchableSelect testid="tc-pu-prov" value={f.pickup_province} onChange={upd("pickup_province")} options={provinces} placeholder="İl" />
                <SearchableSelect testid="tc-pu-dist" value={f.pickup_district} onChange={upd("pickup_district")} options={puDist} placeholder="İlçe" disabled={!f.pickup_province} />
                <Input placeholder="Açık adres" value={f.pickup_address} onChange={upd("pickup_address")} data-testid="tc-pu-addr" />
                <LocationPicker
                  testid="tc-pu-map"
                  height={200}
                  value={f.pickup_lat ? { lat: f.pickup_lat, lng: f.pickup_lng } : null}
                  currentContext={{ province: f.pickup_province, district: f.pickup_district }}
                  onChange={({ lat, lng, province, district, address, road }) => {
                    setF((s) => {
                      const nxt = { ...s, pickup_lat: lat, pickup_lng: lng };
                      if (province && !s.pickup_province) nxt.pickup_province = province;
                      if (district && !s.pickup_district) nxt.pickup_district = district;
                      if (!s.pickup_address) nxt.pickup_address = road || address || "";
                      return nxt;
                    });
                  }}
                />
              </div>
              <div className="rounded-xl border p-3 space-y-2">
                <div className="font-semibold text-sm">Teslim (Nereye)</div>
                <SearchableSelect testid="tc-de-prov" value={f.delivery_province} onChange={upd("delivery_province")} options={provinces} placeholder="İl" />
                <SearchableSelect testid="tc-de-dist" value={f.delivery_district} onChange={upd("delivery_district")} options={deDist} placeholder="İlçe" disabled={!f.delivery_province} />
                <Input placeholder="Açık adres" value={f.delivery_address} onChange={upd("delivery_address")} data-testid="tc-de-addr" />
                <LocationPicker
                  testid="tc-de-map"
                  height={200}
                  value={f.delivery_lat ? { lat: f.delivery_lat, lng: f.delivery_lng } : null}
                  currentContext={{ province: f.delivery_province, district: f.delivery_district }}
                  onChange={({ lat, lng, province, district, address, road }) => {
                    setF((s) => {
                      const nxt = { ...s, delivery_lat: lat, delivery_lng: lng };
                      if (province && !s.delivery_province) nxt.delivery_province = province;
                      if (district && !s.delivery_district) nxt.delivery_district = district;
                      if (!s.delivery_address) nxt.delivery_address = road || address || "";
                      return nxt;
                    });
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div><Label>Kategori</Label>
                <Select value={f.transport_category} onValueChange={upd("transport_category")}>
                  <SelectTrigger data-testid="tc-cat" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Adet</Label><Input type="number" min={1} value={f.quantity} onChange={upd("quantity")} className="mt-1" /></div>
              <div><Label>Durum</Label>
                <Select value={f.running_condition} onValueChange={upd("running_condition")}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div><Label>Marka / Model (ops.)</Label><Input value={f.make_model} onChange={upd("make_model")} className="mt-1" /></div>
              <div><Label>En Geç Alınma Tarihi</Label><Input type="datetime-local" value={f.deadline_at} onChange={upd("deadline_at")} className="mt-1" data-testid="tc-deadline" required /></div>
            </div>
            <div><Label>Açıklama (ops.)</Label><Textarea value={f.description} onChange={upd("description")} rows={2} data-testid="tc-desc" /></div>
            <Button type="submit" disabled={busy} data-testid="tc-submit" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Talebi Yayınla
            </Button>
          </form>
        </TabsContent>

        {/* ================= TALEPLERİM ================= */}
        <TabsContent value="track">
          <YhCustomerLinked onHasItems={setHasYh} />
          <div className="grid gap-2">
            {mine.length === 0 && !hasYh && (
              <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                <PackageOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
                Henüz talep yok. <button className="text-primary underline" onClick={() => changeTab("new")}>Yeni talep aç</button>
              </div>
            )}
            {mine.map((r) => {
              const offs = offersByReq[r.id] || [];
              const pending = offs.filter((o) => o.status === "pending").length;
              const accepted = offs.find((o) => o.status === "accepted");
              return (
                <button
                  key={r.id}
                  onClick={() => openDetail(r.id)}
                  data-testid={`tc-req-${r.id}`}
                  className="text-left rounded-2xl border p-3 hover:border-primary/60 transition-colors"
                >
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <div className="text-sm font-semibold"><b>{r.pickup_province}/{r.pickup_district}</b> → <b>{r.delivery_province}/{r.delivery_district}</b></div>
                    <Badge variant={trStatusVariant(r.status)} data-testid={`tc-req-status-${r.id}`}>{TRANSPORT_REQUEST_STATUS_TR[r.status] || r.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{r.transport_category} · {r.quantity} adet · {r.running_condition}</div>
                  <div className="text-xs text-muted-foreground">Son alım: {new Date(r.deadline_at).toLocaleString("tr-TR")}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className="text-primary font-semibold">{offs.length} teklif</span>
                    {pending > 0 && <span>{pending} beklemede</span>}
                    {accepted && <span className="text-emerald-600 font-semibold">{accepted.business_name} · {accepted.price_tl} TL</span>}
                    <span className="ml-auto text-primary">Detay →</span>
                  </div>
                </button>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* ================= DETAY MODAL ================= */}
      {detailReq && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-3 sm:p-4">
          <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="tc-detail">
            <div className="sticky top-0 z-10 bg-card border-b p-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">Talep Detayı</div>
                <div className="text-lg font-extrabold truncate">{detailReq.pickup_province}/{detailReq.pickup_district} → {detailReq.delivery_province}/{detailReq.delivery_district}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={closeDetail} data-testid="tc-detail-close"><X className="w-5 h-5" /></Button>
            </div>

            <div className="p-4 space-y-4">
              {/* Timeline */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Süreç</div>
                <div className="flex items-center gap-1 overflow-x-auto pb-1">
                  {TRANSPORT_STEPS.filter((s) => s.key !== "OPEN" || detailReq.status === "OPEN").map((s, i, arr) => {
                    const currentIdx = transportStepIndex(detailReq.status);
                    const myIdx = TRANSPORT_STEPS.findIndex((x) => x.key === s.key);
                    const done = myIdx <= currentIdx && detailReq.status !== "CANCELLED";
                    return (
                      <div key={s.key} className="flex items-center shrink-0">
                        <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold ${done ? "bg-primary text-[hsl(var(--brand-black))]" : "bg-muted text-muted-foreground"}`}>{myIdx + 1}</div>
                        <div className={`ml-1 text-[10px] font-medium ${done ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</div>
                        {i < arr.length - 1 && <div className={`w-3 h-0.5 mx-1 ${done ? "bg-primary" : "bg-muted"}`} />}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1"><Badge variant={trStatusVariant(detailReq.status)}>{TRANSPORT_REQUEST_STATUS_TR[detailReq.status] || detailReq.status}</Badge></div>
              </div>

              {/* Info */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-xl border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Alım</div>
                  <div className="font-semibold">{detailReq.pickup_province}/{detailReq.pickup_district}</div>
                  <div className="text-muted-foreground">{detailReq.pickup_address || "—"}</div>
                </div>
                <div className="rounded-xl border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Teslim</div>
                  <div className="font-semibold">{detailReq.delivery_province}/{detailReq.delivery_district}</div>
                  <div className="text-muted-foreground">{detailReq.delivery_address || "—"}</div>
                </div>
                <div className="rounded-xl border p-2 col-span-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Yük</div>
                  <div>{detailReq.transport_category} · {detailReq.quantity} adet · {detailReq.running_condition} {detailReq.make_model ? `· ${detailReq.make_model}` : ""}</div>
                  <div className="text-muted-foreground">Son alım: {new Date(detailReq.deadline_at).toLocaleString("tr-TR")}</div>
                </div>
              </div>

              {/* Accepted provider */}
              {acceptedOffer && (
                <div className="rounded-xl border-2 border-primary/60 bg-primary/5 p-3">
                  <div className="text-xs uppercase tracking-wider text-primary font-bold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Anlaşılan Firma</div>
                  <div className="font-semibold text-base mt-1">{acceptedOffer.business_name}</div>
                  <div className="text-xs text-muted-foreground">⭐ {acceptedOffer.business_rating || 0} · Alım: {new Date(acceptedOffer.pickup_at).toLocaleString("tr-TR")}</div>
                  <div className="mt-1 text-primary font-extrabold">Anlaşılan: {Number(acceptedOffer.price_tl || detailReq.accepted_price_tl || 0).toLocaleString("tr-TR")} TL</div>
                </div>
              )}

              {/* Assigned vehicle & driver */}
              {(detailReq.assigned_vehicle || detailReq.assigned_driver) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {detailReq.assigned_vehicle && (
                    <div className="rounded-xl border p-2 text-xs" data-testid="tc-assigned-vehicle">
                      <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Car className="w-3 h-3" /> Atanan Araç</div>
                      <div className="font-semibold">{detailReq.assigned_vehicle.name}</div>
                      <div className="text-muted-foreground">{detailReq.assigned_vehicle.vehicle_type}{detailReq.assigned_vehicle.capacity ? ` · ${detailReq.assigned_vehicle.capacity}` : ""}</div>
                    </div>
                  )}
                  {detailReq.assigned_driver && (
                    <div className="rounded-xl border p-2 text-xs" data-testid="tc-assigned-driver">
                      <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Atanan Şoför</div>
                      <div className="font-semibold">{detailReq.assigned_driver.name}</div>
                      <div className="text-muted-foreground">İletişim Yol Hattı üzerinden</div>
                    </div>
                  )}
                </div>
              )}

              {/* Pending offers — rejected/withdrawn/pending/accepted history preserved */}
              {(() => {
                const pending = detailOffers.filter((o) => o.status === "pending");
                const rejected = detailOffers.filter((o) => o.status === "rejected");
                const accepted = detailOffers.filter((o) => o.status === "accepted");
                const withdrawn = detailOffers.filter((o) => o.status === "withdrawn");
                const total = detailOffers.length;
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Gelen Teklifler ({total})</div>
                      {total > 0 && (
                        <div className="text-[11px] text-muted-foreground">
                          {pending.length > 0 && <span>{pending.length} Bekleyen</span>}
                          {pending.length > 0 && (rejected.length > 0 || accepted.length > 0 || withdrawn.length > 0) && <span> · </span>}
                          {accepted.length > 0 && <span className="text-emerald-600">{accepted.length} Kabul</span>}
                          {accepted.length > 0 && (rejected.length > 0 || withdrawn.length > 0) && <span> · </span>}
                          {rejected.length > 0 && <span className="text-destructive">{rejected.length} Reddedilen</span>}
                          {rejected.length > 0 && withdrawn.length > 0 && <span> · </span>}
                          {withdrawn.length > 0 && <span className="text-muted-foreground">{withdrawn.length} Geri Çekildi</span>}
                        </div>
                      )}
                    </div>
                    <div className="grid gap-2">
                      {total === 0 && <p className="text-xs text-muted-foreground">Henüz teklif yok. Yeni teklif geldiğinde bildirim alacaksınız.</p>}
                      {detailOffers.map((o) => {
                        const isPending = o.status === "pending";
                        const isRejected = o.status === "rejected";
                        const isAccepted = o.status === "accepted";
                        const isWithdrawn = o.status === "withdrawn";
                        return (
                          <div key={o.id} className={`rounded-lg border p-2 flex items-center justify-between text-sm gap-2 ${isRejected ? "border-destructive/40 bg-destructive/5 opacity-70" : isAccepted ? "border-emerald-500/40 bg-emerald-500/5" : isWithdrawn ? "border-muted bg-muted/30 opacity-60" : "border-amber-500/40 bg-amber-500/5"}`} data-testid={`tc-offer-${o.id}`}>
                            <div className="min-w-0">
                              <div className="font-semibold truncate">{o.business_name} <span className="text-xs">⭐ {o.business_rating || 0} ({o.business_review_count || 0})</span></div>
                              <div className="text-xs text-muted-foreground">{o.vehicle_type} · {o.vehicle_capacity || "—"} · <span className="text-primary font-bold">{Number(o.price_tl).toLocaleString("tr-TR")} TL</span></div>
                              <div className="text-xs">Alım: {new Date(o.pickup_at).toLocaleString("tr-TR")}</div>
                              <div className="text-[10px] mt-0.5">
                                {isPending && <span className="text-amber-600 font-semibold">● Yanıt Bekliyor</span>}
                                {isRejected && <span className="text-destructive font-semibold">● Reddedildi</span>}
                                {isAccepted && <span className="text-emerald-600 font-semibold">● Kabul Edildi</span>}
                                {isWithdrawn && <span className="text-muted-foreground font-semibold">● Sağlayıcı Tarafından Geri Çekildi</span>}
                              </div>
                            </div>
                            {isPending && detailReq.status === "OPEN" && (
                              <div className="flex gap-1 shrink-0">
                                <Button size="sm" onClick={() => acceptOffer(o.id)} data-testid={`tc-accept-${o.id}`} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90">Kabul Et</Button>
                                <Button size="sm" variant="ghost" onClick={() => rejectOffer(o.id)}>Reddet</Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t flex-wrap">
                {detailReq.status === "OPEN" && (
                  <Button variant="ghost" onClick={() => cancelReq(detailReq.id)} className="text-destructive">Talebi İptal Et</Button>
                )}
                {detailReq.status === "DELIVERED" && (
                  <Button onClick={() => confirmDelivery(detailReq.id)} data-testid="tc-confirm-delivery" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Teslimatı Onayla
                  </Button>
                )}
                {detailReq.status === "COMPLETED" && !detailReq.has_reviewed && (
                  <Button onClick={() => { setReviewFor(detailReq); setReviewRating(5); setReviewComment(""); }} data-testid="tc-open-review" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
                    <Star className="w-4 h-4 mr-1" /> Değerlendir
                  </Button>
                )}
                {detailReq.status === "COMPLETED" && detailReq.has_reviewed && (
                  <div className="rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 px-3 py-1 text-xs font-semibold flex items-center gap-1" data-testid="tc-reviewed">
                    <Star className="w-3 h-3 fill-current" /> Değerlendirildi ({detailReq.review_rating}/5)
                  </div>
                )}
                {acceptedOffer && (detailReq.pickup_lat || detailReq.delivery_lat) && (
                  <Button variant="outline" asChild className="ml-auto">
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${detailReq.delivery_lat},${detailReq.delivery_lng}`} target="_blank" rel="noopener noreferrer">
                      <NavIcon className="w-4 h-4 mr-1" /> Teslim Yol Tarifi
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= REVIEW MODAL ================= */}
      {reviewFor && (
        <div className="fixed inset-0 bg-black/60 z-[60] grid place-items-center p-4">
          <div className="bg-card rounded-2xl w-full max-w-md p-4" data-testid="tc-review-modal">
            <div className="font-bold text-lg mb-2">Taşımayı Değerlendirin</div>
            <div className="text-xs text-muted-foreground mb-3">{reviewFor.pickup_province}/{reviewFor.pickup_district} → {reviewFor.delivery_province}/{reviewFor.delivery_district}</div>
            <div className="flex items-center gap-1 mb-3">
              {[1,2,3,4,5].map((n) => (
                <button type="button" key={n} onClick={() => setReviewRating(n)} data-testid={`tc-star-${n}`}>
                  <Star className={`w-8 h-8 ${n <= reviewRating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                </button>
              ))}
            </div>
            <Textarea data-testid="tc-review-comment" value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Deneyiminizi paylaşın (opsiyonel)" rows={3} />
            <div className="flex gap-2 justify-end mt-3">
              <Button variant="outline" onClick={() => setReviewFor(null)} className="rounded-full">Vazgeç</Button>
              <Button onClick={() => submitTransportReview(reviewFor.id, reviewRating, reviewComment)} data-testid="tc-review-submit" className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">Yorumu Gönder</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
